import { Button } from '@nautilusbio/nb-design-system/Button';
import { Group } from '@nautilusbio/nb-design-system/Group';
import { Popover } from '@nautilusbio/nb-design-system/Popover';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import clsx from 'clsx';
import { Shield, Trash2 } from 'lucide-react';
import React, { FC, RefObject, memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from '../styles.module.css';

/**
 * Represents a user with access to a study
 */
interface AllowedUser {
  id: string;
  full_name: string;
  is_owner: boolean;
}

/**
 * Props for the ActionsCellRenderer component
 */
interface ActionsCellRendererProps {
  /** User data for the current row */
  data: AllowedUser;
  /** Whether the current user has permission to manage other users */
  canManage: boolean;
  /** Reference to the container element for popover positioning */
  containerRef: RefObject<HTMLDivElement | null>;
  /** Callback to promote a user to owner status */
  onPromoteToOwner: (userId: string) => void;
  /** Callback to revoke a user's access */
  onRevokeAccess: (userId: string) => void;
  /** Translation function from react-i18next */
  t: ReturnType<typeof useTranslation>['t'];
}

const popoverClassName = clsx(styles.studyContainer, styles.studyPopover);

/**
 * Cell renderer for the "Actions" column in the user management table.
 * Provides buttons to promote users to owners and revoke access, with confirmation popovers.
 *
 * Uses memoization to prevent unnecessary re-renders when data hasn't changed.
 *
 * @param props - Component props
 * @returns Rendered action buttons or null if user cannot manage
 */
const ActionsCellRendererComponent: FC<ActionsCellRendererProps> = ({
  data: user,
  canManage,
  containerRef,
  onPromoteToOwner,
  onRevokeAccess,
  t,
}) => {
  const [isPromotePopoverOpen, setIsPromotePopoverOpen] = useState(false);
  const [isRevokePopoverOpen, setIsRevokePopoverOpen] = useState(false);

  // Extract container element for popover positioning
  // oxlint-disable-next-line refs -- containerRef anchors to a long-lived, already-mounted DOM node used only for popover portal positioning, not live-reactive render output
  const containerElement = containerRef.current ?? undefined;

  if (!canManage) return null;

  return (
    <Stack
      alignItems="center"
      fullHeight
      fullWidth
      gap={1}
      justifyContent="center"
      orientation="horizontal"
      role="group"
      aria-label={t('actions', { ns: 'general' })}
    >
      {!user.is_owner && (
        <Popover
          data-testid="actions-cell-promote-popover"
          content={
            <Stack className={popoverClassName} gap={2} orientation="vertical">
              <Text as="h4">{t('user_management.promote_title', { ns: 'general' })}</Text>
              <Text as="p">{t('user_management.promote_description', { name: user.full_name, ns: 'general' })}</Text>
              <Group fullWidth orientation="horizontal">
                <Button
                  data-testid={`promote-owner-${user.id}`}
                  fullWidth
                  onPress={() => {
                    onPromoteToOwner(user.id);
                    setIsPromotePopoverOpen(false);
                  }}
                  size="xs"
                  visual="primary"
                >
                  {t('confirm', { ns: 'general' })}
                </Button>
                <Button
                  data-testid={`promote-cancel-${user.id}`}
                  fullWidth
                  onPress={() => setIsPromotePopoverOpen(false)}
                  size="xs"
                >
                  {t('cancel', { ns: 'general' })}
                </Button>
              </Group>
            </Stack>
          }
          isOpen={isPromotePopoverOpen}
          onClickOutside={() => setIsPromotePopoverOpen(false)}
          positions={['left', 'right']}
        >
          <Button
            aria-label={t('user_management.promote_tooltip', { ns: 'general' })}
            data-testid={`promote-trigger-${user.id}`}
            onPress={() => setIsPromotePopoverOpen(true)}
            size="xs"
          >
            <Tooltip
              content={t('user_management.promote_tooltip', { ns: 'general' })}
              isDisabled={isPromotePopoverOpen}
              placement="top"
              root={containerElement}
              trigger={<Shield size={16} aria-hidden="true" />}
              triggerClassName={styles.studyTooltip}
            />
          </Button>
        </Popover>
      )}

      <Popover
        data-testid="actions-cell-popover"
        content={
          <Stack className={popoverClassName} gap={2} orientation="vertical">
            <Text as="h4">{t('user_management.revoke_title', { ns: 'general' })}</Text>
            <Text as="p">{t('user_management.revoke_description', { name: user.full_name, ns: 'general' })}</Text>
            {user.is_owner && <Text as="p">{t('user_management.revoke_owner_note', { ns: 'general' })}</Text>}
            <Group fullWidth orientation="horizontal">
              <Button
                data-testid={`revoke-confirm-${user.id}`}
                fullWidth
                onPress={() => {
                  onRevokeAccess(user.id);
                  setIsRevokePopoverOpen(false);
                }}
                size="xs"
                visual="danger"
              >
                {t('confirm', { ns: 'general' })}
              </Button>
              <Button
                data-testid={`revoke-cancel-${user.id}`}
                fullWidth
                onPress={() => setIsRevokePopoverOpen(false)}
                size="xs"
              >
                {t('cancel', { ns: 'general' })}
              </Button>
            </Group>
          </Stack>
        }
        isOpen={isRevokePopoverOpen}
        onClickOutside={() => setIsRevokePopoverOpen(false)}
        positions={['left', 'right']}
      >
        <Button
          aria-label={t('user_management.revoke_tooltip', { ns: 'general' })}
          data-testid={`revoke-trigger-${user.id}`}
          onPress={() => setIsRevokePopoverOpen(true)}
          size="xs"
          visual="danger"
        >
          <Tooltip
            content={t('user_management.revoke_tooltip', { ns: 'general' })}
            isDisabled={isRevokePopoverOpen}
            placement="top"
            root={containerElement}
            trigger={<Trash2 size={16} aria-hidden="true" />}
            triggerClassName={styles.studyTooltip}
          />
        </Button>
      </Popover>
    </Stack>
  );
};

/**
 * Memoized version of ActionsCellRenderer.
 * Only re-renders when user ID, owner status, or management permission changes.
 */
export const ActionsCellRenderer = memo(
  ActionsCellRendererComponent,
  (prev, next) =>
    prev.data.id === next.data.id && prev.data.is_owner === next.data.is_owner && prev.canManage === next.canManage,
);
